import React from 'react';
import { buildHourGrid, type PosterColumn } from '../../../utils/fixedRoutes/stopPosterData';
import type { PosterProps } from './posterTypes';

/**
 * Venice Vaporetto at-stop poster (template T2 / milestone M1.5).
 *
 * Modelled on the ACTV waterbus stop posters: one stacked block per service/
 * direction — a coloured line badge, a route strip of onward stops (from
 * "you are here"), and an hour × minutes departure grid (hours across, minutes
 * stacked). Compact and language-agnostic; scales to many stops via onward-only
 * capping with mid-elision.
 *
 * Plain divs + inline styles for print predictability. Branding placeholder (M2).
 */

const NAVY = '#0f2d3d';
// Fallback badge/strip colours when a route has no colour of its own.
const PALETTE = ['#c0653f', '#6a3d9a', '#33a02c', '#1f4e9b', '#b5651d', '#0d7377'];

const STRIP_COL = 72; // px per stop column
const STRIP_MAX = 8; // max stop columns before eliding the middle

const clamp2: React.CSSProperties = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical' as React.CSSProperties['WebkitBoxOrient'],
  overflow: 'hidden',
};

function badgeText(col: PosterColumn): string {
  if (col.serviceNumber) return col.serviceNumber;
  // Fallback: initials of the destination (e.g. "to Albade Grocery" -> "AG").
  const dest = col.destinationLabel.replace(/^to\s+/i, '');
  const initials = dest
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
  return initials || '•';
}

type StripItem = { name: string; here: boolean } | { ellipsis: true };

function stripItems(onwardStops: string[]): StripItem[] {
  const stops = onwardStops.length > 0 ? onwardStops : [];
  if (stops.length <= STRIP_MAX) {
    return stops.map((name, i) => ({ name, here: i === 0 }));
  }
  const head = stops.slice(0, STRIP_MAX - 2).map((name, i) => ({ name, here: i === 0 }));
  return [...head, { ellipsis: true }, { name: stops[stops.length - 1], here: false }];
}

function RouteStrip({ onwardStops, color }: { onwardStops: string[]; color: string }) {
  const items = stripItems(onwardStops);
  const cellStyle = (w: number): React.CSSProperties => ({ width: w, flex: `0 0 ${w}px` });

  return (
    <div style={{ overflow: 'hidden' }}>
      {/* Labels band (fixed height so angled/long names never escape the block) */}
      <div style={{ display: 'flex' }}>
        {items.map((it, i) =>
          'ellipsis' in it ? (
            <div key={i} style={cellStyle(40)} />
          ) : (
            <div key={i} style={cellStyle(STRIP_COL)}>
              <div
                style={{
                  height: 36,
                  display: 'flex',
                  alignItems: 'flex-end',
                  justifyContent: 'center',
                  textAlign: 'center',
                }}
              >
                <span
                  title={it.name}
                  style={{
                    fontFamily: 'Arial, sans-serif',
                    fontSize: 9,
                    fontWeight: it.here ? 700 : 400,
                    color: '#333',
                    lineHeight: '11px',
                    ...clamp2,
                  }}
                >
                  {it.name}
                </span>
              </div>
            </div>
          )
        )}
      </div>

      {/* Line + dots */}
      <div style={{ position: 'relative', height: 14, display: 'flex', alignItems: 'center' }}>
        <div
          style={{
            position: 'absolute',
            left: STRIP_COL / 2,
            right: STRIP_COL / 2 + 22,
            top: 6,
            height: 3,
            background: color,
            zIndex: 0,
          }}
        />
        <div style={{ display: 'flex', position: 'relative', zIndex: 1 }}>
          {items.map((it, i) =>
            'ellipsis' in it ? (
              <div
                key={i}
                style={{
                  ...cellStyle(40),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color,
                  fontSize: 18,
                  lineHeight: '12px',
                }}
              >
                ⋯
              </div>
            ) : (
              <div key={i} style={{ ...cellStyle(STRIP_COL), display: 'flex', justifyContent: 'center' }}>
                <div
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    background: it.here ? color : '#fff',
                    border: `2px solid ${color}`,
                  }}
                />
              </div>
            )
          )}
        </div>
        <div style={{ color, fontSize: 16, marginLeft: 8, lineHeight: '12px' }}>➜</div>
      </div>

      {/* "You are here" marker under the origin dot */}
      <div style={{ display: 'flex' }}>
        {items.map((it, i) =>
          !('ellipsis' in it) && it.here ? (
            <div
              key={i}
              style={{
                ...cellStyle(STRIP_COL),
                textAlign: 'center',
                fontFamily: 'Arial, sans-serif',
                fontSize: 8,
                fontWeight: 700,
                color,
              }}
            >
              YOU ARE HERE
            </div>
          ) : (
            <div key={i} style={cellStyle('ellipsis' in it ? 40 : STRIP_COL)} />
          )
        )}
      </div>
    </div>
  );
}

function HourGrid({ times }: { times: string[] }) {
  const { hours, rows } = buildHourGrid(times);
  if (hours.length === 0) return null;
  return (
    <table style={{ borderCollapse: 'collapse', tableLayout: 'fixed' }}>
      <thead>
        <tr>
          {hours.map((h) => (
            <th
              key={h}
              style={{
                background: '#dfe7ef',
                border: '1px solid #cfd8e3',
                font: '600 11px Arial',
                padding: '3px 4px',
                width: 26,
              }}
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, r) => (
          <tr key={r}>
            {row.map((mm, c) => (
              <td
                key={c}
                style={{
                  border: '1px solid #eef2f6',
                  font: '12px Arial',
                  textAlign: 'center',
                  color: '#222',
                  padding: '1px 2px',
                }}
              >
                {mm ?? ''}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ServiceBlock({ col, color }: { col: PosterColumn; color: string }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 14,
        alignItems: 'flex-start',
        padding: '14px 16px',
        borderBottom: '2px solid #e5e5e5',
      }}
    >
      {/* Left: direction + badge */}
      <div style={{ width: 150, flexShrink: 0 }}>
        <div style={{ font: '600 10px Arial', color: '#555' }}>Direction</div>
        <div style={{ font: '700 13px Arial', color: '#222', marginBottom: 8 }}>
          {col.destinationLabel.replace(/^to\s+/i, '')}
        </div>
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: color,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            font: '700 20px Arial',
          }}
        >
          {badgeText(col)}
        </div>
      </div>

      {/* Right: strip + hour grid(s) */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <RouteStrip onwardStops={col.onwardStops} color={color} />
        {col.dayGroups.map((group, gi) => {
          const first = group.times[0];
          const last = group.times[group.times.length - 1];
          return (
            <div key={gi} style={{ marginTop: 12 }}>
              {col.dayGroups.length > 1 ? (
                <div style={{ font: '700 11px Arial', color: '#333', marginBottom: 4 }}>{group.label}</div>
              ) : null}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ overflowX: 'auto' }}>
                  <HourGrid times={group.times} />
                </div>
                <div style={{ flexShrink: 0, font: '11px Arial', color: '#444', textAlign: 'center' }}>
                  🕐
                  <br />
                  first {first}
                  <br />
                  last {last}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function VeniceVaporettoPoster({
  data,
  validFrom,
  brandColor = NAVY,
  infoLine = 'For timetable information visit your local transport authority website.',
}: PosterProps) {
  return (
    <div
      className="venice-poster"
      style={{
        width: 794,
        minHeight: 1123,
        boxSizing: 'border-box',
        background: '#fff',
        color: '#111',
        fontFamily: 'Arial, Helvetica, sans-serif',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid #ddd',
      }}
    >
      {/* Header */}
      <div
        style={{
          background: brandColor,
          color: '#fff',
          padding: '14px 18px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div>
          <div style={{ fontSize: 13, opacity: 0.8 }}>This Stop</div>
          <div style={{ fontSize: 28, fontWeight: 700, lineHeight: 1.1 }}>{data.stopName}</div>
          <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>Valid from {validFrom}</div>
        </div>
        <div
          aria-hidden
          title="QR to live departures (placeholder)"
          style={{
            width: 60,
            height: 60,
            border: '2px dashed #789',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 10,
            color: '#cbd5e1',
          }}
        >
          QR
        </div>
      </div>

      {/* Service blocks */}
      <div style={{ flex: 1 }}>
        {data.columns.length === 0 ? (
          <div style={{ padding: 24, color: '#666', fontSize: 15 }}>
            No scheduled departures found for this stop.
          </div>
        ) : (
          data.columns.map((col, i) => (
            <ServiceBlock key={col.routeId} col={col} color={col.routeColor || PALETTE[i % PALETTE.length]} />
          ))
        )}
      </div>

      {/* Footer */}
      <div style={{ padding: '10px 16px', fontSize: 11, color: '#888', borderTop: `3px solid ${brandColor}` }}>
        {infoLine}
        <span style={{ color: '#aaa' }}> · Operator logo / help text / T&amp;C — placeholder (branding, M2)</span>
      </div>
    </div>
  );
}

export default VeniceVaporettoPoster;
